import { useState, useEffect, useCallback, useMemo } from 'react';
import type { PaginatedResponse } from '@/types/api';
import type { TableSettings } from '@/types/table';

interface ResourceFilters {
  search?: string;
  instance_id?: number;
  endpoint_id?: number;
  status?: string;
  stack_id?: number;
  type?: string;
  driver?: string;
}

interface UseResourceDataResult<T> {
  data: T[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_FILTERS: ResourceFilters = {};

export function useResourceData<T>(
  resource: string,
  filters?: ResourceFilters,
  settings?: TableSettings
): UseResourceDataResult<T> {
  const stableFilters = useMemo(() => filters || EMPTY_FILTERS, [filters]);
  
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  // A changed request (new params or a refetch) restarts loading; adjusted during render, not in the effect.
  const [request, setRequest] = useState({ resource, stableFilters, settings, reloadKey });
  if (
    request.resource !== resource ||
    request.stableFilters !== stableFilters ||
    request.settings !== settings ||
    request.reloadKey !== reloadKey
  ) {
    setRequest({ resource, stableFilters, settings, reloadKey });
    setLoading(true);
    setError(null);
  }

  useEffect(() => {
    const fetchData = async () => {
      const params = new URLSearchParams();

      if (stableFilters.search) params.set('search', stableFilters.search);
      if (stableFilters.instance_id) params.set('instance_id', stableFilters.instance_id.toString());
      if (stableFilters.endpoint_id) params.set('endpoint_id', stableFilters.endpoint_id.toString());
      if (stableFilters.status) params.set('status', stableFilters.status);
      if (stableFilters.stack_id) params.set('stack_id', stableFilters.stack_id.toString());
      if (stableFilters.type) params.set('type', stableFilters.type);
      if (stableFilters.driver) params.set('driver', stableFilters.driver);

      if (settings) {
        params.set('limit', settings.limit.toString());
        params.set('offset', settings.offset.toString());
        params.set('sort_by', settings.sortBy);
        params.set('sort_order', settings.sortOrder);
      }

      const apiKey = localStorage.getItem('apiKey') || '';
      const query = params.toString();

      try {
        const response = await fetch(`/api/v1/${resource}${query ? '?' + query : ''}`, {
          headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' }
        });
        const json = await response.json();

        if (!response.ok) {
          throw new Error(json.error?.message || 'API Error');
        }

        if (json.data && 'total' in json.data && 'data' in json.data) {
          const paginatedData = json.data as PaginatedResponse<T>;
          setData(Array.isArray(paginatedData.data) ? paginatedData.data : []);
          setTotal(paginatedData.total || 0);
        } else if (Array.isArray(json.data)) {
          setData(json.data);
          setTotal(json.data.length);
        } else {
          setData([]);
          setTotal(0);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [resource, stableFilters, settings, reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  return { data, total, loading, error, refetch };
}